import { useState, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { scraperApi } from '../services/scraperApi';
import type {
  ContainerMonitor,
  StatusChangeLog,
  MonitorStats,
  MonitorStatusChangeEvent,
  MonitorLfdChangeEvent,
} from '../types/scraper';

/**
 * What one PCC lookup ended up saying, in a shape the panel can render
 * without knowing whether the query succeeded.
 *
 * A failed lookup is NOT an exception here: "PCC link not set" and "no browser
 * online" are the two most likely outcomes on a fresh tenant, and both are
 * things the user can fix — they belong on screen, not in a swallowed catch.
 */
export interface PccCheckOutcome {
  ok: boolean;
  message: string;
  /** Only meaningful when ok: the container really is at the exam station. */
  arrived: boolean;
}

// ==================== State & Actions Interfaces ====================

interface ContainerMonitorState {
  monitors: ContainerMonitor[];
  stats: MonitorStats | null;
  recentChanges: StatusChangeLog[];
  loading: boolean;
  error: string | null;
}

interface ContainerMonitorActions {
  fetchMonitors: (filters?: Record<string, string>) => Promise<void>;
  refreshAll: () => Promise<void>;
  refreshSingle: (monitorId: number) => Promise<void>;
  refreshSelected: (monitorIds: number[]) => Promise<void>;
  checkPcc: (monitorId: number) => Promise<PccCheckOutcome>;
  stopMonitor: (monitorId: number, reason?: string) => Promise<void>;
  batchStopMonitors: (monitorIds: number[], reason?: string) => Promise<void>;
  startMonitor: (containerNumber: string, orderId?: string) => Promise<void>;
  fetchHistory: (monitorId: number) => Promise<StatusChangeLog[]>;
}

interface UseContainerMonitorOptions {
  enabled?: boolean;
  autoRefreshInterval?: number; // ms, default 30000
}

// ==================== Hook ====================

export function useContainerMonitor(
  options: UseContainerMonitorOptions = {}
): ContainerMonitorState & ContainerMonitorActions {
  const { enabled = true, autoRefreshInterval = 30000 } = options;

  const [monitors, setMonitors] = useState<ContainerMonitor[]>([]);
  const [stats, setStats] = useState<MonitorStats | null>(null);
  const [recentChanges, setRecentChanges] = useState<StatusChangeLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const socketRef = useRef<Socket | null>(null);

  // Fetch monitors with optional filters
  const fetchMonitors = useCallback(async (filters?: Record<string, string>) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const [monitorsData, statsData, changesData] = await Promise.all([
        scraperApi.getMonitors(filters),
        scraperApi.getStats(),
        scraperApi.getRecentChanges(10),
      ]);

      // Prevent stale update
      if (requestId !== requestIdRef.current) return;

      setMonitors(monitorsData);
      setStats(statsData);
      setRecentChanges(changesData);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load monitors');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Refresh all (same as fetch without filters)
  const refreshAll = useCallback(async () => {
    await fetchMonitors();
  }, [fetchMonitors]);

  // Refresh single monitor
  const refreshSingle = useCallback(async (monitorId: number) => {
    try {
      await scraperApi.triggerCheck(monitorId);
      // Re-fetch to get updated data
      await fetchMonitors();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Check failed');
    }
  }, [fetchMonitors]);

  // Refresh selected monitors
  const refreshSelected = useCallback(async (monitorIds: number[]) => {
    try {
      await scraperApi.triggerBatchCheck(monitorIds);
      await fetchMonitors();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Batch check failed');
    }
  }, [fetchMonitors]);

  /**
   * Ask PCC about one container now. Separate from refreshSingle on purpose:
   * that one asks the TERMINAL, and for a container customs has already hauled
   * off the dock the terminal has nothing left to say.
   *
   * Returns the outcome instead of setting `error`: this is one row's answer,
   * and the panel shows it next to that row rather than as a page-level error.
   */
  const checkPcc = useCallback(async (monitorId: number): Promise<PccCheckOutcome> => {
    try {
      const result = await scraperApi.triggerPccCheck(monitorId);
      // The row itself changes only when PCC had something new (dates, or the
      // push to CBP Hold) — re-fetch either way so "Checked" and the chip
      // can't disagree with the sentence we just showed.
      await fetchMonitors();
      return { ok: true, message: result.message, arrived: result.arrived };
    } catch (err) {
      const failure = err as { code?: string; response?: { data?: { message?: string } } };
      // Giving up waiting is not the same as the lookup failing: the server
      // keeps going and writes the answer, and the panel's own poll picks it
      // up within 30s. Saying "failed" here would send someone re-clicking a
      // query that is still running.
      const message = failure.code === 'ECONNABORTED'
        ? 'PCC is taking a while — the answer will appear on this row shortly'
        : failure.response?.data?.message
          || (err instanceof Error ? err.message : 'PCC check failed');
      return { ok: false, message, arrived: false };
    }
  }, [fetchMonitors]);

  // Stop a single monitor
  const stopMonitor = useCallback(async (monitorId: number, reason?: string) => {
    try {
      await scraperApi.stopMonitor(monitorId, reason);
      await fetchMonitors();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Stop failed');
    }
  }, [fetchMonitors]);

  // Batch stop monitors
  const batchStopMonitors = useCallback(async (monitorIds: number[], reason?: string) => {
    try {
      await scraperApi.batchStopMonitors(monitorIds, reason);
      await fetchMonitors();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Batch stop failed');
    }
  }, [fetchMonitors]);

  // Start monitoring a container
  const startMonitor = useCallback(async (containerNumber: string, orderId?: string) => {
    try {
      await scraperApi.startMonitor(containerNumber, orderId);
      await fetchMonitors();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Start failed');
    }
  }, [fetchMonitors]);

  // Fetch history for a specific monitor
  const fetchHistory = useCallback(async (monitorId: number): Promise<StatusChangeLog[]> => {
    try {
      return await scraperApi.getHistory(monitorId, 30);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load history');
      return [];
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (enabled) {
      fetchMonitors();
    }
  }, [enabled, fetchMonitors]);

  // Auto-refresh list + stats on interval. Socket events only fire on data
  // *change*, but the "Checked" column needs to refresh even when nothing
  // changed so the user can see monitoring is alive. Poll the list too.
  useEffect(() => {
    if (!enabled || !autoRefreshInterval) return;

    const intervalId = setInterval(async () => {
      try {
        const [monitorsData, statsData, changesData] = await Promise.all([
          scraperApi.getMonitors(),
          scraperApi.getStats(),
          scraperApi.getRecentChanges(10),
        ]);
        setMonitors(monitorsData);
        setStats(statsData);
        setRecentChanges(changesData);
      } catch {
        // Silent failure for background refresh
      }
    }, autoRefreshInterval);

    return () => clearInterval(intervalId);
  }, [enabled, autoRefreshInterval]);

  // Socket.io real-time updates
  useEffect(() => {
    if (!enabled) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    // Anchored strip — see useExtensionEnrollment.ts.
    const socket = io(import.meta.env.VITE_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:4000', {
      auth: { token },
    });
    socketRef.current = socket;

    const handlers: Array<[string, (...args: unknown[]) => void]> = [];

    const on = (event: string, handler: (...args: unknown[]) => void) => {
      socket.on(event, handler);
      handlers.push([event, handler]);
    };

    on('container:status_changed', (data: unknown) => {
      const event = data as MonitorStatusChangeEvent;
      // Update the specific monitor in local state.
      // terminal_site + terminal_site_confirmed must be updated too — the
      // first check that confirms a terminal also flips the monitor from
      // "Unknown" to the actual terminal, and without this the UI's
      // "Terminal" column stays on "Unknown" until the next 30s poll.
      setMonitors(prev =>
        prev.map(m =>
          m.id === event.monitorId
            ? {
                ...m,
                latest_status: event.newStatus,
                latest_holds: event.holds,
                // Reasons travel with the tags; patching one without the other
                // would leave the Holds column describing the previous check.
                latest_hold_details: event.holdDetails ?? m.latest_hold_details,
                latest_lfd: event.lfd,
                lfd_alert_level: event.lfdAlertLevel,
                terminal_site: (event.terminalSite as typeof m.terminal_site) ?? m.terminal_site,
                terminal_site_confirmed: event.terminalSite ? true : m.terminal_site_confirmed,
                last_changed_at: new Date().toISOString(),
                last_checked_at: new Date().toISOString(),
              }
            : m
        )
      );
      // Add to recent changes
      setRecentChanges(prev => [
        {
          id: Date.now(),
          monitor_id: event.monitorId,
          container_number: event.containerNumber,
          terminal_site: event.terminalSite,
          old_status: event.oldStatus,
          new_status: event.newStatus,
          old_holds: null,
          new_holds: event.holds,
          new_hold_details: event.holdDetails ?? null,
          old_lfd: null,
          new_lfd: event.lfd,
          raw_data: null,
          source: 'fetch_op',
          checked_at: new Date().toISOString(),
          order_id: event.orderId,
        },
        ...prev.slice(0, 9),
      ]);
    });

    on('container:lfd_changed', (data: unknown) => {
      const event = data as MonitorLfdChangeEvent;
      setMonitors(prev =>
        prev.map(m =>
          m.id === event.monitorId
            ? { ...m, latest_lfd: event.newLfd, lfd_alert_level: event.alertLevel }
            : m
        )
      );
    });

    return () => {
      for (const [event, handler] of handlers) {
        socket.off(event, handler);
      }
      socket.disconnect();
      socketRef.current = null;
    };
  }, [enabled]);

  return {
    monitors,
    stats,
    recentChanges,
    loading,
    error,
    fetchMonitors,
    refreshAll,
    refreshSingle,
    refreshSelected,
    checkPcc,
    stopMonitor,
    batchStopMonitors,
    startMonitor,
    fetchHistory,
  };
}
